import * as React from "react";
import { useSnackbar } from "notistack";
import { Button, FormControl, Grid, InputLabel, MenuItem, Select, TextField } from "@material-ui/core";
import { useAuctionClosedReport, useClosedReportOptions, DefaultClosedAuctionReportRequest } from "../../services/ReportsService";
import { ContainerStyles } from "./styles/ContainerStyles";
import { SearchSkeleton } from "../auction-management/SearchSkeleton";
import { downloadCSV } from "./DownloadCsv";
import { Filter } from "../../helpers/Filter";
import { LoadingComponent } from "../loading/LoadingComponent";
import { auctionStatusGroupingOptionString } from "../../services/AuctionLotMgmtService";

export const ClosedReportContainer: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const classes = ContainerStyles();

  const [filterOptions, isOptionsLoading, ,] = useClosedReportOptions(() =>
    enqueueSnackbar("Could not retrieve list of filter options", { variant: "error" })
  );
  const [filterRequest, setFilterRequest, apiPostAsync, filterResponse, isLoadingReport, ,] = useAuctionClosedReport(() =>
    enqueueSnackbar("Could not retrieve list of auctions", { variant: "error" })
  );

  React.useEffect(() => {
    setFilterRequest(DefaultClosedAuctionReportRequest());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (filterResponse && filterResponse?.requestId === filterRequest?.requestId) {
      downloadCSV(filterResponse.byteArray, "ClosedAuctionReport.csv");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterResponse]);

  const changeHandler = (event: any, property: string) => {
    if (filterRequest) {
      if (event.target.value === "All" && property === "auctionStatusGrouping") {
        setFilterRequest({
          ...filterRequest,
          auctionStatusGrouping: ["Closed", "ClosedButNotSold", "ClosedAndSold"],
          requestId: Date.now.toString(),
        });
      } else if (event.target.value === "All") {
        setFilterRequest({ ...filterRequest, [property]: null, requestId: Date.now.toString() });
      } else if (property === "auctionStatusGrouping") {
        setFilterRequest({ ...filterRequest, auctionStatusGrouping: [event.target.value], requestId: Date.now.toString() });
      } else {
        setFilterRequest({ ...filterRequest, [property]: event.target.value, requestId: Date.now.toString() });
      }
    }
  };

  const changeDirection = (sortDescending: boolean) => {
    if (filterRequest) {
      setFilterRequest({ ...filterRequest, sortDescending: sortDescending, requestId: Date.now.toString() });
    }
  };

  const changeDate = (event: any, property: string) => {
    if (filterRequest) {
      setFilterRequest({ ...filterRequest, [property]: event.target.value, requestId: Date.now.toString() });
    }
  };

  const search = () => {
    if (filterRequest !== null) {
      apiPostAsync();
    }
  };

  const reset = () => {
    if (filterRequest !== null) {
      setFilterRequest(DefaultClosedAuctionReportRequest());
    }
  };

  return (
    <>
      {isLoadingReport && <LoadingComponent label="Loading report" />}
      {isOptionsLoading && <SearchSkeleton />}
      {!isLoadingReport && (
        <Grid container spacing={1}>
          <Grid item xs={12} lg={2}>
            {!isOptionsLoading && filterOptions?.locations !== null && (
              <Filter
                title="Location"
                filterOptions={filterOptions?.locations}
                filterPropertyName="location"
                changeHandler={changeHandler}
                filterValue={filterRequest?.location ? filterRequest.location : ""}
              />
            )}
          </Grid>

          <Grid item xs={12} lg={2}>
            {!isOptionsLoading && filterOptions?.auctionStatusGroupings !== null && (
              <FormControl className={classes.width100} variant="outlined" margin="dense">
                <InputLabel variant="outlined">Status</InputLabel>
                <Select
                  label={"status"}
                  value={
                    filterRequest?.auctionStatusGrouping && filterRequest.auctionStatusGrouping.length === 1
                      ? filterRequest.auctionStatusGrouping
                      : ""
                  }
                  onChange={value => changeHandler(value, "auctionStatusGrouping")}
                  variant="outlined"
                >
                  <MenuItem value={"All"}>All</MenuItem>
                  {filterOptions?.auctionStatusGroupings &&
                    filterOptions?.auctionStatusGroupings.map((type: string, index: number) => (
                      <MenuItem value={type} key={index}>
                        {auctionStatusGroupingOptionString(type)}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            )}
          </Grid>

          <Grid item xs={12} lg={3}>
            {!isOptionsLoading && filterOptions?.insuranceCompanies !== null && (
              <Filter
                title="Client"
                filterOptions={filterOptions?.insuranceCompanies}
                filterPropertyName="insuranceCompany"
                changeHandler={changeHandler}
                filterValue={filterRequest?.insuranceCompany ? filterRequest.insuranceCompany : ""}
              />
            )}
          </Grid>
          <Grid item xs={12} lg={2}>
            {!isOptionsLoading && (
              <TextField
                variant="outlined"
                id="date"
                label="Date From"
                type="date"
                margin="dense"
                value={filterRequest?.dateFrom ? filterRequest?.dateFrom : ""}
                InputLabelProps={{
                  shrink: true,
                }}
                onChange={event => changeDate(event, "dateFrom")}
                fullWidth
              />
            )}
          </Grid>

          <Grid item xs={12} lg={2}>
            {!isOptionsLoading && (
              <TextField
                variant="outlined"
                id="date"
                label="Date To"
                type="date"
                margin="dense"
                value={filterRequest?.dateTo ? filterRequest?.dateTo : ""}
                InputLabelProps={{
                  shrink: true,
                }}
                onChange={event => changeDate(event, "dateTo")}
                fullWidth
              />
            )}
          </Grid>

          <Grid item xs={12} lg={2}>
            {!isOptionsLoading && filterOptions?.sortBy !== null && (
              <Filter
                title="Sort by"
                filterOptions={filterOptions?.sortBy}
                filterPropertyName="sortBy"
                changeHandler={changeHandler}
                filterValue={filterRequest?.sortBy ? filterRequest.sortBy : ""}
                enumCase={true}
                all={false}
              />
            )}
          </Grid>

          <Grid item xs={12} lg={2}>
            {!isOptionsLoading && (
              <Filter
                title="Direction"
                filterOptions={["Descending", "Ascending"]}
                filterPropertyName="sortDescending"
                changeHandler={value => changeDirection(value.target.value === "Descending")}
                filterValue={filterRequest?.sortDescending ? "Descending" : "Ascending"}
                all={false}
              />
            )}
          </Grid>

          <Grid item xs={12} lg={1}>
            {!isOptionsLoading && (
              <Button color="primary" className={classes.searchButton} onClick={reset}>
                Reset
              </Button>
            )}
          </Grid>

          <Grid item xs={12}>
            {!isOptionsLoading && (
              <Button className={classes.createReportButton} variant="contained" color="primary" onClick={search} fullWidth>
                Create Report
              </Button>
            )}
          </Grid>
        </Grid>
      )}
    </>
  );
};
